import { LinkIcon } from "lucide-react";
import { FC } from "react";
import { Link } from "react-router";

import BuilderIcon from "@/assets/logos/builder.svg?react";
import LegitimateIcon from "@/assets/logos/LogoSymbol.svg?react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { SKUPageType } from "@/data/types";
import { ChooseBuilderDialog } from "@/dialogs/choose-builder-dialog";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";

const getLabel = (pageType: SKUPageType) => {
  switch (pageType) {
    case SKUPageType.LEGITIMATE:
      return "Legitimate";
    case SKUPageType.BUILDER_IO:
      return "Builder.io";
    case SKUPageType.REDIRECT:
      return "Redirect";
    default:
      return null;
  }
};

const getDescription = (pageType: SKUPageType) => {
  switch (pageType) {
    case SKUPageType.LEGITIMATE:
      return "Build the digital experience using Legitimate";
    case SKUPageType.BUILDER_IO:
      return "Integrate with Builder, an external CMS";
    case SKUPageType.REDIRECT:
      return "Reroute to a different URL with tag parameters";
    default:
      return null;
  }
};

const getIcon = (pageType: SKUPageType) => {
  switch (pageType) {
    case SKUPageType.LEGITIMATE:
      return <LegitimateIcon className="size-4" />;
    case SKUPageType.BUILDER_IO:
      return <BuilderIcon className="size-4" />;
    case SKUPageType.REDIRECT:
      return <LinkIcon className="size-4" />;
    default:
      return null;
  }
};

type Props = {
  skuId: string;
  pageType: SKUPageType;
};

export const WebpageBuilderSelector: FC<Props> = ({ skuId, pageType }) => (
  <Card>
    <CardHeader>
      <h4 className="text-lg font-medium leading-none">Webpage Builder</h4>
      <p className="text-sm text-muted-foreground">
        Select how you would like to build your digital experience.
      </p>
    </CardHeader>
    <CardContent>
      <div className="flex flex-col xl:flex-row justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="text-sm">{getIcon(pageType)}</div>
            <p className="text-sm font-medium">{getLabel(pageType)}</p>
          </div>
          <p className="text-sm text-muted-foreground">
            {getDescription(pageType)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <ChooseBuilderDialog skuId={skuId} pageType={pageType} />
          {pageType === SKUPageType.LEGITIMATE && (
            <Button asChild>
              <Link
                to={`/skus/${skuId}/web-experience`}
                onClickCapture={() => {
                  trackEvent(AnalyticsEvent.LEGITIMATE_CMS_EDITED);
                }}
              >
                Edit webpage
              </Link>
            </Button>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);
